import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, ProgressBar, Tooltip } from 'react-bootstrap';

import { Text } from 'Components/Text/Text';
import { ICON_NEGATIVE, ICON_POSITIVE } from 'Config/Icons';
import { ModuleEntities, OrganizationsAndAssessmentSubjectsTableRowData } from 'Models/AssessmentSubject';
import { AssessmentWorkflowState } from 'Models/DueDiligenceWorkflow';

import styles from './DueDiligenceWorkflowProgressTableCellContent.module.css';

/**
 * Renders in one of the following ways:
 * * In the case where there is no assessment subject for an organization: nothing.
 * * In the case where a due diligence workflow has been completed, and the next due diligence workflow has not yet begun: text describing the workflow status and whether a follow-up workflow has been scheduled.
 * * In all other cases: text describing the next step in the due diligence workflow to be completed, a corresponding progress bar, and a tooltip/hover describing the steps represented by the progress bar.
 *
 * TODO [TPRM - managing third parties and services]: Test this component.
 */
export const DueDiligenceWorkflowProgressTableCellContent = (props: { rowData: OrganizationsAndAssessmentSubjectsTableRowData<ModuleEntities> }) => {
    const { assessmentSubjectData } = props.rowData;

    if (!assessmentSubjectData) {
        return <></>;
    } else if (assessmentSubjectData.assessmentSubject.last_assessed && assessmentSubjectData.assessmentSubject.assessment_workflow_data.state === AssessmentWorkflowState.AWAITING_ASSESSMENT) {
        return (
            <div className={styles.primaryTextAndSecondaryText}>
                <Text noStyles>Assessment completed</Text>
                <Text noStyles color="darkGray">
                    {assessmentSubjectData.assessmentSubject.assessment_workflow_setup.due_date ? 'Reassessment scheduled' : 'No reassessment scheduled'}
                </Text>
            </div>
        );
    }

    const steps = assessmentSubjectData.dueDiligenceWorkflowProgress.steps;
    const completedStepNumber = assessmentSubjectData.dueDiligenceWorkflowProgress.completedStepNumber;

    const getProgressBarPropsForStep = (step: number) => ({
        now: 100 / steps.length,
        className: step <= completedStepNumber ? styles.progressBarSegmentComplete : styles.progressBarSegmentIncomplete,
    });

    const tooltipContent = (
        <div className={styles.tooltipContent}>
            {steps.map((step, index) => (
                <TooltipRow key={index} step={index} progress={completedStepNumber} text={step} />
            ))}
        </div>
    );

    return (
        <div className={styles.progressBarAndText}>
            <OverlayTrigger placement="auto" delay={{ show: 100, hide: 200 }} overlay={<Tooltip id="information">{tooltipContent}</Tooltip>}>
                <ProgressBar className={styles.progressBarContainer}>
                    {steps.map((_, index) => (
                        <ProgressBar key={index} {...getProgressBarPropsForStep(index)} />
                    ))}
                </ProgressBar>
            </OverlayTrigger>
            <div className={styles.progressText}>
                <Text noStyles color="darkGray">
                    {assessmentSubjectData.dueDiligenceWorkflowProgress.inProgressStepDescription}
                </Text>
            </div>
        </div>
    );
};

const TooltipRow = ({ step, progress, text }: { step: number; progress: number; text: string }) => {
    const isComplete = step <= progress;

    return (
        <div className={styles.tooltipRow}>
            <FontAwesomeIcon title={`${text}: ${isComplete ? 'Complete' : 'Incomplete'}`} icon={isComplete ? ICON_POSITIVE : ICON_NEGATIVE} className={styles.tooltipIcon} />
            <div className={styles.progressText}>
                <Text noStyles color="white">
                    {text}
                </Text>
            </div>
        </div>
    );
};
